/**
 * ZipCodeField - Zip code input with tax calculation
 *
 * Features:
 * - Validates 5-digit zip format
 * - Shows spinner while calculating tax
 * - Displays "calculating tax" message
 * - Blocks form submission until tax is calculated
 */

import type { UseFormRegisterReturn } from 'react-hook-form';
import { FormField } from './FormField';

interface ZipCodeFieldProps {
  /** react-hook-form registration object */
  registration: UseFormRegisterReturn;
  /** Error message from form validation */
  error?: string;
  /** Whether tax is being calculated */
  isCalculating?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
}

export function ZipCodeField({
  registration,
  error,
  isCalculating,
  disabled,
}: ZipCodeFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <FormField
        label="Zip code"
        name={registration.name}
        ref={registration.ref}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={5}
        placeholder="12345"
        autoComplete="postal-code"
        onChange={registration.onChange}
        onBlur={registration.onBlur}
        error={error}
        disabled={disabled || isCalculating}
        rightAdornment={
          isCalculating ? (
            <svg
              className="h-5 w-5 animate-spin text-slate-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : undefined
        }
      />
      {isCalculating && (
        <p className="text-xs text-slate-500">Calculating tax...</p>
      )}
    </div>
  );
}

export default ZipCodeField;
